import { Injectable, inject } from '@angular/core';
import { DialogService } from '~core/services/dialog.service';
import { Observable, filter, tap } from 'rxjs';
import { ResourceFacade } from '~backoffice/app/~store/facades/resource.facade';
import { GlobalFacade } from '~backoffice/app/~store/facades/global.facade';
import { SnackbarService } from '~backoffice/app/~shared/services/snackbar.service';
import { Tag } from '~core/models/core.models';
import { DeleteConfirmComponent } from '~backoffice/app/~shared/components/modals/delete-confirm/delete-confirm.component';
import { TagService } from '~core/services/tag.service';
import { EditTagComponent } from '~backoffice/app/tags/modals/edit-tag/edit-tag.component';

@Injectable()
export class TagManagementService {
  private resourceFacade = inject(ResourceFacade);
  private tagService = inject(TagService);
  private globalFacade = inject(GlobalFacade);

  constructor(
    private snackBar: SnackbarService,
    private dialogService: DialogService
  ) {}

  edit(tag?: Tag): Observable<unknown> {
    this.globalFacade.setFormViolations([]);
    const dialogRef = this.dialogService.open({
      component: EditTagComponent,
      config: {
        panelClass: ['mat-dialog-custom-full-screen'],
        disableClose: true,
        height: '100vh',
        width: '100vw',
        maxWidth: '100vw'
      }
    });

    dialogRef.componentInstance.tag = tag;
    return dialogRef.afterClosed();
  }

  delete(tag: Tag): Observable<unknown> {
    const delete$ = this.tagService.delete(tag.id);
    const dialogRef = this.dialogService.open({
      component: DeleteConfirmComponent,
      config: {
        panelClass: ['mat-dialog-custom'],
        disableClose: true
      }
    });

    dialogRef.componentInstance.removeObservable$ = delete$;
    dialogRef.componentInstance.wordEntry = tag.title;

    return dialogRef.afterClosed().pipe(
      filter(Boolean),
      tap({
        next: () => {
          this.resourceFacade.getData({ refresh: true });
          this.snackBar.open({
            text: 'Tag supprimé',
            type: 'success'
          });
        }
      })
    );
  }

  toggleDefaultStatus(tag: Tag, oldState?: boolean): Observable<Tag> {
    const newData = {
      markedAsDefault: oldState ? false : true
    };
    return this.tagService.update(tag.id, newData).pipe(
      tap({
        next: () => {
          this.resourceFacade.getData({ refresh: true });
          this.snackBar.open({
            text: 'Statut du tag mis à jour',
            type: 'success'
          });
        }
      })
    );
  }

  toggleReplayStatus(tag: Tag, oldState?: boolean): Observable<Tag> {
    const newData = {
      markedAsReplay: oldState ? false : true
    };
    return this.tagService.update(tag.id, newData).pipe(
      tap({
        next: () => {
          this.resourceFacade.getData({ refresh: true });
          this.snackBar.open({
            text: 'Statut de replay du tag mis à jour',
            type: 'success'
          });
        }
      })
    );
  }

  toggleShowVideoViews(tag: Tag, oldState?: boolean): Observable<Tag> {
    const newData = {
      showVideoViews: oldState ? false : true
    };
    return this.tagService.update(tag.id, newData).pipe(
      tap({
        next: () => {
          this.resourceFacade.patchItem({ item: tag, newData: newData });
          this.snackBar.open({
            text: 'Statut du nombre de vues sur les lignes de vidéos mis à jour',
            type: 'success'
          });
        },
        error: () => {
          this.snackBar.open({
            text: 'Une erreur est survenue, veuillez réessayer',
            type: 'error'
          });
        }
      })
    );
  }
}
